import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { MessageCircle } from "lucide-react";

import { Button } from "../ui/button";
import { FacebookIcon, InstagramIcon } from "../MetaIcons";
import { getFacebookAppId, getFacebookConfigId } from "../../config";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Login da Meta pelo SDK oficial do navegador.
//
// O react-facebook-login, usado no WhatiCket original, está parado desde 2020
// e não funciona no React 19 (usa a API de classes legada). O SDK carregado
// direto faz o mesmo e não adiciona dependência.

const ESCOPO_FACEBOOK = [
  "pages_show_list",
  "pages_messaging",
  "pages_manage_metadata",
  "pages_read_engagement",
  "business_management"
].join(",");

// A API do Instagram tem duas variantes, com permissões distintas:
//
//   login do Instagram  -> instagram_business_*, app próprio, login em
//                          instagram.com
//   login do Facebook   -> instagram_basic + instagram_manage_messages, conta
//                          ligada a uma Página, token da Página
//
// Aqui é a segunda: quem responde no Direct é a Página dona da conta, e é o
// token dela que o webhook e a Send API usam. Pedir as `instagram_business_*`
// neste fluxo devolve "Invalid Scopes".
const ESCOPO_INSTAGRAM = [
  "instagram_basic",
  "instagram_manage_messages",
  ESCOPO_FACEBOOK
].join(",");

// Coexistência: o número segue atendido no aplicativo do celular e, ao mesmo
// tempo, pela Cloud API. Sem whatsapp_business_management não dá para assinar
// os campos que trazem o que foi respondido pelo aparelho.
const ESCOPO_WHATSAPP = [
  "whatsapp_business_messaging",
  "whatsapp_business_management",
  "business_management"
].join(",");

const carregarSdk = (appId) =>
  new Promise((resolve, reject) => {
    if (window.FB) {
      resolve(window.FB);
      return;
    }

    window.fbAsyncInit = () => {
      window.FB.init({ appId, cookie: true, xfbml: false, version: "v21.0" });
      resolve(window.FB);
    };

    const script = document.createElement("script");

    script.src = "https://connect.facebook.net/pt_BR/sdk.js";
    script.async = true;
    script.defer = true;
    script.crossOrigin = "anonymous";
    script.onerror = () => reject(new Error("Falha ao carregar o SDK da Meta"));

    document.body.appendChild(script);
  });

const MetaLoginButtons = () => {
  // O app id vem de Configurações, pela API. Antes vinha de variável de
  // ambiente gravada no build — e bastava alguém esquecer de repassá-la ao
  // contêiner para os botões sumirem sem erro nenhum, que foi o que
  // aconteceu em produção. O `.env` fica só como reserva.
  const [appId, setAppId] = useState(getFacebookAppId());
  const [configIds, setConfigIds] = useState(null);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    let ativo = true;

    api
      .get("/meta/app-config")
      .then(({ data }) => {
        if (!ativo) return;

        if (data?.appId) setAppId(data.appId);
        setConfigIds(data?.configIds || null);
      })
      .catch(() => {
        // Sem configuração salva, segue o que houver no ambiente.
      });

    return () => {
      ativo = false;
    };
  }, []);

  useEffect(() => {
    if (appId) carregarSdk(appId).catch(() => null);
  }, [appId]);

  const ESCOPOS = {
    facebook: ESCOPO_FACEBOOK,
    instagram: ESCOPO_INSTAGRAM,
    whatsapp: ESCOPO_WHATSAPP
  };

  const conectar = useCallback(
    async (canal) => {
      setCarregando(true);

      try {
        const FB = await carregarSdk(appId);

        // App de Login for Business referencia a configuração criada no
        // painel; app do login clássico continua pedindo as permissões soltas.
        // Misturar os dois faz a Meta recusar com "Invalid Scopes".
        const configId = configIds?.[canal] || getFacebookConfigId(canal);

        const resposta = await new Promise((resolve) =>
          FB.login(
            resolve,
            configId
              ? { config_id: configId, response_type: "token" }
              : { scope: ESCOPOS[canal], return_scopes: true }
          )
        );

        // Quem fecha a janela sem autorizar não é erro: só não conectou.
        if (!resposta?.authResponse) {
          setCarregando(false);
          return;
        }

        const { userID, accessToken } = resposta.authResponse;

        await api.post("/facebook", {
          facebookUserId: userID,
          facebookUserToken: accessToken,
          addInstagram: canal === "instagram",
          addWhatsapp: canal === "whatsapp"
        });

        toast.success(
          {
            facebook: "Página do Facebook conectada.",
            instagram: "Instagram conectado.",
            whatsapp: "WhatsApp oficial conectado, com coexistência ligada."
          }[canal]
        );
      } catch (err) {
        toastError(err);
      } finally {
        setCarregando(false);
      }
    },
    [appId, configIds]
  );

  // Sem app configurado não há login possível: mostrar os botões só geraria
  // um erro no clique.
  if (!appId) return null;

  return (
    <>
      <Button
        variant="outline"
        disabled={carregando}
        onClick={() => conectar("facebook")}
      >
        <FacebookIcon className="h-4 w-4 text-[#1877f2]" />
        Facebook
      </Button>
      <Button
        variant="outline"
        disabled={carregando}
        onClick={() => conectar("whatsapp")}
        title="Número do WhatsApp Business API, seguindo atendido também pelo celular"
      >
        <MessageCircle className="h-4 w-4 text-emerald-500" />
        WhatsApp oficial
      </Button>
      <Button
        variant="outline"
        disabled={carregando}
        onClick={() => conectar("instagram")}
      >
        <InstagramIcon className="h-4 w-4 text-[#e1306c]" />
        Instagram
      </Button>
    </>
  );
};

export default MetaLoginButtons;
